import { Day, DayColor } from './month-view.component';

/**
 * Color used when multiple absences exist on the same day
 */
export const MULTIPLE_ABSENCES_COLOR: DayColor = 'DarkGrey';

/**
 * Get the color for a day based on absence status
 */
export function getDayColor(status: string): DayColor {
  switch (status) {
    case 'Approved':
      return 'Green';
    case 'Denied':
      return 'Red';
    case 'Pending':
      return 'Yellow';
    case 'Event':
      return 'Teal';
    default:
      return 'LightGrey';
  }
}

/**
 * Generate a consistent date key for indexing absences
 */
export function getDateKey(date: Date): string {
  return date.toDateString();
}

/**
 * Apply absence data to calendar days
 * @param days Array of calendar days to update
 * @param absencesByDate Map of absences indexed by date key
 */
export function applyAbsencesToDays(
  days: Day[],
  absencesByDate: Map<string, any[]> | undefined,
): void {
  for (const day of days) {
    const dateKey = getDateKey(day.date);
    const absences = absencesByDate?.get(dateKey);

    if (absences?.length > 0) {
      // Set data for days with absences
      if (absences.length === 1) {
        day.data = absences[0];
        day.color = getDayColor(absences[0].status);
      } else {
        // Multiple absences on the same day
        day.data = absences;
        day.color = getColorForMultipleAbsences(absences);
      }
    } else {
      // Clear data for days without absences
      day.data = null;
      day.color = null;
    }
  }
}

/**
 * Determine the color for a day with multiple absences.
 * If all absences have the same status, use that status color.
 * Otherwise, use the multiple absences color (DarkGrey).
 */
export function getColorForMultipleAbsences(absences: any[]): DayColor {
  const statuses = new Set<string>();

  for (const absence of absences) {
    statuses.add(absence.status);
  }

  // If all absences have the same status, use the status color
  if (statuses.size === 1) {
    const singleStatus = Array.from(statuses)[0];
    return getDayColor(singleStatus);
  }

  // Multiple different statuses - use dark grey
  return MULTIPLE_ABSENCES_COLOR;
}

/**
 * Check if two dates represent the same month and year
 */
export function isSameMonthAndYear(date1: Date, date2: Date): boolean {
  return (
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
}
